<template>
  <div>
    <div class="sales-report">
      <div class="row mar-bt-15">
        <div class="col-md-12">
          <div class="filters-applied-row">
            <el-button type="primary" size="small" @click="openFiltersModal" style="margin-right: 10px;">
              {{ $t('orders.filters') }}
            </el-button>
            <template v-if="appliedFilters.length">
              <span class="filters-applied-label">{{ $t('orders.filtersList') }}:</span>
              <span
                v-for="(flt, idx) in appliedFilters"
                :key="'pos-order-actions-filter-' + idx"
                class="filter-selection">
                {{ flt }}
              </span>
              <a href="#" @click.prevent="resetFilters()" class="reset-filters-link">{{ $t('orders.resetFilters') }}</a>
            </template>
            <template v-else>
              <span class="filters-applied-label">{{ $t('orders.noFiltersApplied') }}</span>
            </template>
          </div>
        </div>
      </div>

      <!-- Filters Modal (same UX as Sales report) -->
      <!-- Same filter modal shell as Orders.vue (filtersAndSortModal; footer inside modal-body) -->
      <vmodal name="posOrderActionsFiltersModal" height="auto" width="1200" :adaptive="true" :scrollable="true" class="filtersAndSortModal sales-report-filters">
        <div class="card">
          <div class="card-content">
            <div class="modal-header slim-header">
              <h4>{{ $t('orders.filters') }}</h4>
            </div>
            <div class="modal-body slim-body">
              <div class="filters-grid sales-report-filters-grid">
                <div class="modal-column-left">
                  <div class="filter-section" v-if="locations.length">
                    <h6>{{ $t('orders.location') }}</h6>
                    <el-select
                      v-model="modalSelectedLocationIds"
                      :placeholder="$t('general.select')"
                      multiple
                      collapse-tags
                      clearable
                      filterable
                      class="w-100"
                      style="width: 100%;">
                      <el-option
                        v-for="loc in locations"
                        :key="loc.true_id"
                        :label="loc.name"
                        :value="loc.true_id">
                      </el-option>
                    </el-select>
                  </div>

                  <div class="filter-section" v-if="tableMaps.length">
                    <h6>{{ $t('settingTable.maps') }}</h6>
                    <el-select
                      v-model="modalSelectedTableMapIds"
                      :placeholder="$t('general.search')"
                      multiple
                      filterable
                      collapse-tags
                      clearable
                      class="w-100"
                      style="width: 100%;">
                      <el-option
                        v-for="m in tableMaps"
                        :key="m.id"
                        :label="m.name || ('ID ' + m.id)"
                        :value="m.id">
                      </el-option>
                    </el-select>
                  </div>

                  <div class="filter-section" v-if="zones.length">
                    <h6>{{ $t('settingTable.zone') }}</h6>
                    <el-select
                      v-model="modalSelectedZoneIds"
                      :placeholder="$t('general.select')"
                      multiple
                      collapse-tags
                      clearable
                      filterable
                      class="w-100"
                      style="width: 100%;">
                      <el-option
                        v-for="z in zones"
                        :key="z.id"
                        :label="z.name"
                        :value="z.id">
                      </el-option>
                    </el-select>
                  </div>

                  <div class="filter-section">
                    <h6>{{ $t('reports.tables') }}</h6>
                    <el-select
                      v-model="modalSelectedTable"
                      ref="tableSelect"
                      value-key="id"
                      filterable
                      remote
                      reserve-keyword
                      clearable
                      class="w-100"
                      style="width: 100%;"
                      :placeholder="$t('settingTable.searchTable')"
                      :remote-method="searchTables"
                      :loading="loadingTables"
                      @change="clearSearch">
                      <el-option
                        v-for="item in mergedTables"
                        :key="item.id"
                        :label="item.label"
                        :value="item">
                      </el-option>
                    </el-select>
                  </div>
                </div>

                <div class="modal-column-right">
                  <div class="filter-section">
                    <h6>{{ $t('general.orderId') }}</h6>
                    <el-input
                      v-model="modalSelectedOrderId"
                      type="number"
                      min="0"
                      placeholder="3823231"
                      class="w-100"
                    />
                  </div>
                  <div class="filter-section" v-if="posUsers.length">
                    <h6>{{ $t('reports.operator') }}</h6>
                    <el-select
                      v-model="modalSelectedPosUser"
                      :placeholder="$t('general.alls')"
                      filterable
                      clearable
                      class="w-100"
                      style="width: 100%;">
                      <el-option
                        v-for="operator in posUsers"
                        :key="operator.id"
                        :label="operator.firstname + ' ' + operator.lastname"
                        :value="operator.id">
                      </el-option>
                    </el-select>
                  </div>

                  <div class="filter-section">
                    <h6>{{ $t('orders.status') }}</h6>
                    <el-select
                      v-model="modalSelectedStatus"
                      :placeholder="$t('general.all')"
                      clearable
                      filterable
                      class="w-100"
                      style="width: 100%;">
                      <el-option :value="null" :label="$t('general.all')" />
                      <el-option
                        v-for="st in orderStatusOptions"
                        :key="'pos-order-actions-status-' + st"
                        :value="st"
                        :label="$t('ordersStatus.' + st)" />
                    </el-select>
                  </div>

                  <div class="filter-section">
                    <h6>{{ $t('general.action') }}</h6>
                    <el-select
                      v-model="modalSelectedActionType"
                      :placeholder="$t('general.all')"
                      clearable
                      filterable
                      class="w-100"
                      style="width: 100%;">
                      <el-option :value="null" :label="$t('general.all')" />
                      <el-option :value="1" :label="$t('reports.posOrderActionTypes.openOrder')" />
                      <el-option :value="2" :label="$t('reports.posOrderActionTypes.markProduct')" />
                      <el-option :value="3" :label="$t('reports.posOrderActionTypes.preNote')" />
                      <el-option :value="4" :label="$t('reports.posOrderActionTypes.closeOrder')" />
                      <el-option :value="5" :label="$t('reports.posOrderActionTypes.storno')" />
                      <el-option :value="6" :label="$t('reports.posOrderActionTypes.transferOut')" />
                      <el-option :value="7" :label="$t('reports.posOrderActionTypes.transferIn')" />
                    </el-select>
                  </div>
                </div>
              </div>

              <div class="modal-footer slim-footer">
                <el-button size="small" @click="resetModalFilters">{{ $t('general.reset') }}</el-button>
                <el-button size="small" type="primary" @click="applyFilters">{{ $t('general.apply') }}</el-button>
              </div>
            </div>
          </div>
        </div>
      </vmodal>

      <div class="col-md-12">
        <div class="pull-left" style="display: flex; flex-wrap: wrap; gap: 10px; align-items: center;">
          <el-date-picker
            type="daterange"
            v-model="period"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :name="pickerNames">
          </el-date-picker>

          <div style="display: flex; align-items: center; gap: 8px;">
            {{ $t('general.from') }}
            <el-time-select
              :placeholder="$t('catalogProd.timePlaceholder')"
              :value="valid_from"
              v-model="valid_from"
              class="mar-rt-5"
              :picker-options="{
                start: '00:00',
                step: '00:10',
                end: '24:00'
              }">
            </el-time-select>
            {{ $t('general.to') }}
            <el-time-select
              :placeholder="$t('catalogProd.timePlaceholder')"
              :value="valid_to"
              v-model="valid_to"
              class="mar-lt-5"
              :picker-options="{
                start: '00:00',
                step: '00:10',
                end: '24:00'
              }">
            </el-time-select>
          </div>

          <div class="pull-left search-button-reports">
            <div :title="$t('reports.search')" class="btn btn-icon btn-info btn-search" @click="updateData">
              <i class="ti-search"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-12" v-if="period && period.length === 2">
        <p style="text-align:left; margin: 20px 0px;">
          {{ $t('reports.interval') }}:
          <b>{{ period[0] }} {{ valid_from }}</b> - <b>{{ period[1] }} {{ valid_to }}</b>
        </p>
      </div>

      <div class="col-md-12">
        <div class="card">
          <div class="card-content total-pagination reports-center-columns" style="width: 100%;">
            <data-tables-server
              @query-change="paginationChanged"
              @sort-change="handleSortChange"
              @row-click="goToOrder"
              v-loading="loading"
              :data="rows"
              :total="total"
              :pagination-props="paginationProps"
              ref="reportsTable"
            >
              <el-table-column class-name="centered-cell" prop="id" :label="$t('reports.orderId')" min-width="100" />
              <el-table-column class-name="centered-cell" :min-width="140" :label="$t('reports.orderSeriesNumber')">
                <template slot-scope="props">
                  {{ formatOrderSeriesNumber(props.row) }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" :min-width="160" :label="$t('reports.location')">
                <template slot-scope="props">
                  {{ orderLocationName(props.row) }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" :min-width="140" :label="$t('reports.posDeviceName')">
                <template slot-scope="props">
                  {{ posDeviceName(props.row.pos_id) }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" :label="$t('settingTable.zone')" min-width="120">
                <template slot-scope="props">
                  {{ (props.row.table_data && props.row.table_data.zone) ? props.row.table_data.zone : '-' }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" :label="$t('reports.tableNumber')" min-width="140">
                <template slot-scope="props">
                  {{ (props.row.table_data && props.row.table_data.number) ? props.row.table_data.number : '-' }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" :min-width="160" :label="$t('reports.waiterName')">
                <template slot-scope="props">
                  {{ waiterDisplayName(props.row.waiter_id) }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" prop="status" :label="$t('orders.status')" min-width="120">
                <template slot-scope="props">
                  <button
                    v-if="rowStatusKey(props.row)"
                    type="button"
                    class="btn pos-order-actions-status-btn"
                    :class="rowStatusKey(props.row)"
                    aria-disabled="true"
                    tabindex="-1">
                    {{ changeDeliveryStatus(rowStatusKey(props.row), props.row.delivery_type) }}
                  </button>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column
                class-name="centered-cell"
                prop="totals_grandtotal"
                sortable="custom"
                :min-width="130"
                :label="$t('orders.totals_grandtotal')"
              >
                <template slot-scope="props">
                  {{ formatMoneyAmount(Number(props.row.totals_grandtotal) || 0) }}
                </template>
              </el-table-column>
              <el-table-column
                class-name="centered-cell"
                prop="transfers_count"
                sortable="custom"
                :min-width="140"
                :label="$t('reports.orderTransfersCount')"
              >
                <template slot-scope="props">
                  {{ props.row.transfers_count != null ? props.row.transfers_count : '-' }}
                </template>
              </el-table-column>
              <el-table-column
                class-name="centered-cell"
                prop="stornos_count"
                sortable="custom"
                :min-width="120"
                :label="$t('reports.orderStornosCount')"
              >
                <template slot-scope="props">
                  {{ props.row.stornos_count != null ? props.row.stornos_count : '-' }}
                </template>
              </el-table-column>
              <el-table-column
                class-name="centered-cell"
                prop="created_at"
                sortable="custom"
                :label="$t('reports.orderCreatedAt')"
                min-width="170"
              >
                <template slot-scope="props">
                  {{ props.row.created_at ? formatToCompanyTimezone(props.row.created_at) : '-' }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" :label="$t('general.action')" min-width="130">
                <template slot-scope="props">
                  <div class="pos-order-actions-actions">
                    <a
                      href="#"
                      class="btn btn-info btn-icon pos-order-actions-action-btn"
                      :title="$t('reports.posOrderActionOpenOrder') || $t('reports.openOrder') || $t('general.open')"
                      @click.prevent.stop="openOrderInNewTab(props.row)"
                    >
                      <i class="ti-new-window"></i>
                    </a>
                    <a
                      href="#"
                      class="btn btn-primary btn-icon pos-order-actions-action-btn"
                      :title="$t('reports.posOrderActionOpenLogs') || $t('general.details') || $t('general.open')"
                      @click.prevent.stop="openLogs(props.row)"
                    >
                      <i class="ti-eye"></i>
                    </a>
                  </div>
                </template>
              </el-table-column>
            </data-tables-server>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import swal from 'sweetalert2'
import { Select, DatePicker } from 'element-ui'
export default {
  components: {
    [Select.name]: Select,
    [DatePicker.name]: DatePicker
  },
  props: {
    isPosOrderActionsReport: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    isPosOrderActionsReport () {
      if (this.isPosOrderActionsReport) {
        this.bootstrap()
      }
    },
    '$route.query.posOrderActionsFilters' () {
      this.applyFiltersFromUrl()
    }
  },
  created () {
    this.applyFiltersFromUrl()
    this.bootstrap()
    this.$bus.$on('refreshPosOrderActions', this.handleRefreshPosOrderActions)
  },
  beforeDestroy () {
    this.$bus.$off('refreshPosOrderActions', this.handleRefreshPosOrderActions)
  },
  data () {
    return {
      valid_from: '05:00',
      valid_to: '05:00',
      timezone: null,
      loading: true,
      loadingTables: false,
      paginationProps: {
        layout: 'prev, pager, next, sizes, total'
      },
      pickerNames: ['date_from', 'date_to'],
      period: [
        moment().subtract(1, 'days').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD')
      ],
      locations: [],
      selectedLocationIds: [],
      posUsers: [],
      selectedPosUser: '',
      tableMaps: [],
      selectedTableMapIds: [],
      selectedZoneIds: [],
      zones: [],
      selectedTable: null,
      availableTables: [],
      rows: [],
      total: 0,
      sortBy: [],
      sortOrder: [],
      posNameById: {},
      selectedActionType: null,
      selectedStatus: null,
      selectedOrderId: null,

      // modal filters (same UX as Sales report)
      isFiltersModalOpen: false,
      modalSelectedLocationIds: [],
      modalSelectedTableMapIds: [],
      modalSelectedZoneIds: [],
      modalSelectedPosUser: '',
      modalSelectedStatus: null,
      modalSelectedActionType: null,
      modalSelectedTable: null,
      modalSelectedOrderId: null
    }
  },
  computed: {
    orderStatusOptions () {
      return [
        'NEW',
        'PROCESSING',
        'ON_HOLD',
        'POSTPONED',
        'AWAITING_TRANSPORT',
        'PICKED_UP_BY_DELIVERY',
        'TRANSPORT',
        'DELIVERED',
        'NOT_DELIVERED',
        'CANCELED'
      ]
    },
    appliedFilters () {
      const out = []

      if (Array.isArray(this.selectedLocationIds) && this.selectedLocationIds.length) {
        const names = this.selectedLocationIds
          .map(id => {
            const found = this.locations.find(l => l.true_id === id || l.id === id)
            return found && found.name ? found.name : String(id)
          })
          .filter(Boolean)
        if (names.length) out.push(this.$t('orders.location') + ': ' + names.join(', '))
      }

      if (Array.isArray(this.selectedTableMapIds) && this.selectedTableMapIds.length) {
        const names = this.selectedTableMapIds
          .map(id => {
            const found = this.tableMaps.find(m => m.id === id)
            return found && found.name ? found.name : String(id)
          })
          .filter(Boolean)
        if (names.length) out.push(this.$t('settingTable.maps') + ': ' + names.join(', '))
      }

      if (Array.isArray(this.selectedZoneIds) && this.selectedZoneIds.length) {
        const names = this.selectedZoneIds
          .map(id => {
            const z = this.zones.find(x => String(x.id) === String(id))
            return z && z.name ? z.name : String(id)
          })
          .filter(Boolean)
        if (names.length) out.push(this.$t('settingTable.zone') + ': ' + names.join(', '))
      }

      if (this.selectedPosUser) {
        const u = this.posUsers.find(x => x.id === this.selectedPosUser)
        const label = u ? `${u.firstname || ''} ${u.lastname || ''}`.trim() : String(this.selectedPosUser)
        out.push(this.$t('reports.operator') + ': ' + (label || String(this.selectedPosUser)))
      }

      if (this.selectedStatus) out.push(this.$t('orders.status') + ': ' + this.$t('ordersStatus.' + this.selectedStatus))

      if (typeof this.selectedActionType === 'number') out.push(this.$t('general.action') + ': ' + this.$t('reports.posOrderActionTypes.' + this.actionTypeKey(this.selectedActionType)))

      if (this.selectedTable && this.selectedTable.id != null) out.push(this.$t('reports.tables') + ': ' + (this.selectedTable.label || ('ID ' + this.selectedTable.id)))

      if (this.selectedOrderId != null && String(this.selectedOrderId).trim() !== '') out.push(this.$t('general.orderId') + ': ' + String(this.selectedOrderId))

      return out
    },
    mergedTables () {
      const map = new Map(this.availableTables.map(t => [t.id, t]))
      if (this.selectedTable && this.selectedTable.id && !map.has(this.selectedTable.id)) {
        map.set(this.selectedTable.id, this.selectedTable)
      }
      return Array.from(map.values())
    }
  },
  methods: {
    handleRefreshPosOrderActions () {
      // Re-fetch using current pagination state (so we stay on the same page).
      const page = this.paginationProps.currentPage || 1
      const pageSize = this.paginationProps.pageSize || 20
      this.getOrders({ page, pageSize })
    },
    actionTypeKey (type) {
      const numericToKey = {
        1: 'openOrder',
        2: 'markProduct',
        3: 'preNote',
        4: 'closeOrder',
        5: 'storno',
        6: 'transferOut',
        7: 'transferIn'
      }
      return numericToKey[Number(type)] || String(type)
    },
    syncModalFromFilters () {
      this.modalSelectedLocationIds = Array.isArray(this.selectedLocationIds) ? [...this.selectedLocationIds] : []
      this.modalSelectedTableMapIds = Array.isArray(this.selectedTableMapIds) ? [...this.selectedTableMapIds] : []
      this.modalSelectedZoneIds = Array.isArray(this.selectedZoneIds) ? [...this.selectedZoneIds] : []
      this.modalSelectedPosUser = this.selectedPosUser || ''
      this.modalSelectedStatus = this.selectedStatus || null
      this.modalSelectedActionType = this.selectedActionType !== undefined ? this.selectedActionType : null
      this.modalSelectedTable = this.selectedTable && this.selectedTable.id != null ? { ...this.selectedTable } : null
      this.modalSelectedOrderId = this.selectedOrderId
    },
    openFiltersModal () {
      this.syncModalFromFilters()
      this.isFiltersModalOpen = true
      this.$modal.show('posOrderActionsFiltersModal')
    },
    applyFilters () {
      const prevLocationIds = Array.isArray(this.selectedLocationIds) ? this.selectedLocationIds.map(String).sort().join(',') : ''
      this.selectedLocationIds = Array.isArray(this.modalSelectedLocationIds) ? [...this.modalSelectedLocationIds] : []
      this.selectedTableMapIds = Array.isArray(this.modalSelectedTableMapIds) ? [...this.modalSelectedTableMapIds] : []
      this.selectedZoneIds = Array.isArray(this.modalSelectedZoneIds) ? [...this.modalSelectedZoneIds] : []
      this.selectedPosUser = this.modalSelectedPosUser || ''
      this.selectedStatus = this.modalSelectedStatus || null
      this.selectedActionType = this.modalSelectedActionType !== undefined ? this.modalSelectedActionType : null
      this.selectedTable = this.modalSelectedTable && this.modalSelectedTable.id != null ? { ...this.modalSelectedTable } : null
      this.selectedOrderId = this.modalSelectedOrderId !== '' && this.modalSelectedOrderId !== null && this.modalSelectedOrderId !== undefined
        ? Number(this.modalSelectedOrderId)
        : null

      // Refresh maps only when location filter actually changed.
      const nextLocationIds = Array.isArray(this.selectedLocationIds) ? this.selectedLocationIds.map(String).sort().join(',') : ''
      if (prevLocationIds !== nextLocationIds) this.getTableMaps()

      this.$modal.hide('posOrderActionsFiltersModal')
      this.isFiltersModalOpen = false
      this.$nextTick(() => this.updateData())
    },
    resetModalFilters () {
      this.resetFilters()
      this.syncModalFromFilters()
    },
    resetFilters () {
      this.selectedLocationIds = []
      this.selectedTableMapIds = []
      this.selectedZoneIds = []
      this.selectedPosUser = ''
      this.selectedStatus = null
      this.selectedActionType = null
      this.selectedTable = null
      this.selectedOrderId = null
      this.availableTables = []
      this.updateData()
    },
    rowStatusKey (row) {
      if (!row) return null
      if (row.status && typeof row.status === 'object' && row.status.id) return row.status.id
      return row.status || null
    },
    changeDeliveryStatus (statusUsed, deliveryType) {
      if (!statusUsed) return '-'
      if (statusUsed === 'DELIVERED' && deliveryType === 'EAT_LOCATION') {
        statusUsed = 'reservedReady'
        return this.$t('ordersStatus.' + statusUsed)
      }
      if (statusUsed === 'DELIVERED' && deliveryType === 'PICK_UP') {
        statusUsed = 'completedReady'
        return this.$t('ordersStatus.' + statusUsed)
      }
      if (statusUsed === 'DELIVERED' && (deliveryType === 'MEET_HALF' || deliveryType === 'DELIVERY')) {
        statusUsed = 'deliveredReady'
        return this.$t('ordersStatus.' + statusUsed)
      }
      return this.$t('ordersStatus.' + statusUsed)
    },
    orderStatusClass (type) {
      switch (type) {
        case 'DELIVERED': return 'success'
        case 'CANCELED': return 'danger'
        case 'NOT_DELIVERED': return 'danger'
        case 'NEW': return 'info'
        case 'ON_HOLD': return 'hold'
        case 'POSTPONED': return 'postponed'
        case 'PROCESSING': return 'warning'
        case 'AWAITING_TRANSPORT': return 'yellow'
        case 'PICKED_UP_BY_DELIVERY': return 'grey'
        case 'TRANSPORT': return 'blue'
        default: return 'primary'
      }
    },
    bootstrap () {
      this.timezone = this.$session.get('selectedCompanyData').timezone || 'Europe/Bucharest'
      this.locations = (this.$session.get('selectedCompanyData').locations_details || [])
      this.getPosUsers()
      this.getPosDevices()
      this.getTableMaps()
      this.getZones()
      this.getOrders()
    },
    handleSortChange ({ prop, order }) {
      if (!order) {
        this.sortBy = []
        this.sortOrder = []
      } else {
        const direction = order === 'ascending' ? 'asc' : 'desc'
        this.sortOrder = [direction]
        this.sortBy = [prop]
      }
      const pageSize = this.paginationProps.pageSize || 20
      this.getOrders({ page: 1, pageSize })
    },
    formatOrderSeriesNumber (row) {
      if (!row) return '-'
      const s = row.series ? String(row.series) : ''
      const n = row.number !== undefined && row.number !== null ? String(row.number) : ''
      if (!s && !n) return '-'
      return [s, n].filter(Boolean).join(' ')
    },
    orderLocationName (row) {
      if (!row) return '-'
      const loc = row.production_details && row.production_details.location && row.production_details.location.name
      if (loc) return loc
      const lid = row.location_id
      if (lid == null) return '-'
      const found = this.locations.find(l => l.true_id === lid || l.id === lid)
      return found && found.name ? found.name : String(lid)
    },
    posDeviceName (posId) {
      if (posId === undefined || posId === null) return '-'
      const name = this.posNameById[posId]
      return name || String(posId)
    },
    waiterDisplayName (waiterId) {
      if (waiterId === undefined || waiterId === null) return '-'
      const u = this.posUsers.find(x => x.id === waiterId)
      if (!u) return String(waiterId)
      const full = `${u.firstname || ''} ${u.lastname || ''}`.trim()
      return full || String(waiterId)
    },
    serializeFiltersForUrl () {
      return JSON.stringify({
        selectedLocationIds: this.selectedLocationIds,
        selectedTableMapIds: this.selectedTableMapIds,
        selectedZoneIds: this.selectedZoneIds,
        selectedPosUser: this.selectedPosUser,
        selectedStatus: this.selectedStatus,
        selectedActionType: this.selectedActionType,
        selectedTable: this.selectedTable,
        selectedOrderId: this.selectedOrderId,
        period: this.period,
        valid_from: this.valid_from,
        valid_to: this.valid_to,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      })
    },
    applyFiltersFromUrl () {
      const raw = this.$route.query && this.$route.query.posOrderActionsFilters
      if (!raw) return
      try {
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return
        if (Array.isArray(parsed.selectedLocationIds)) this.selectedLocationIds = parsed.selectedLocationIds
        if (Array.isArray(parsed.selectedTableMapIds)) this.selectedTableMapIds = parsed.selectedTableMapIds
        // Backwards compatible with older saved URLs that used `selectedZone` (single).
        if (Array.isArray(parsed.selectedZoneIds)) this.selectedZoneIds = parsed.selectedZoneIds
        else if (parsed.selectedZone !== undefined && parsed.selectedZone !== null && String(parsed.selectedZone).trim() !== '') this.selectedZoneIds = [parsed.selectedZone]
        if (parsed.selectedPosUser !== undefined) this.selectedPosUser = parsed.selectedPosUser
        if (parsed.selectedStatus !== undefined) this.selectedStatus = parsed.selectedStatus
        if (parsed.selectedActionType !== undefined) this.selectedActionType = parsed.selectedActionType
        if (parsed.selectedTable !== undefined) this.selectedTable = parsed.selectedTable
        if (parsed.selectedOrderId !== undefined) this.selectedOrderId = parsed.selectedOrderId
        if (Array.isArray(parsed.period)) this.period = parsed.period
        if (typeof parsed.valid_from === 'string') this.valid_from = parsed.valid_from
        if (typeof parsed.valid_to === 'string') this.valid_to = parsed.valid_to
        if (Array.isArray(parsed.sortBy)) this.sortBy = parsed.sortBy
        if (Array.isArray(parsed.sortOrder)) this.sortOrder = parsed.sortOrder
      } catch (e) {
        // ignore invalid url state
      }
    },
    goToOrder (row) {
      if (!row || row.id == null) return
      const route = {
        path: `/reports/pos-order-actions/${row.id}`,
        query: {
          tab: 'pos-order-actions',
          posOrderActionsFilters: this.serializeFiltersForUrl(),
          app_id: row.app_id
        }
      }
      this.$router.push(route)
    },
    openLogs (row) {
      this.goToOrder(row)
    },
    openOrderInNewTab (row) {
      if (!row || row.id == null) return
      // window.open(`/#/orders/view/${row.id}`)
      this.$router.push('/orders/view/' + row.id)
    },
    formatToCompanyTimezone (utcString) {
      const date = new Date(utcString + 'Z')
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
      const parts = formatter.formatToParts(date)
      return `${parts.find(p => p.type === 'year').value}-${
        parts.find(p => p.type === 'month').value}-${
        parts.find(p => p.type === 'day').value} ${
        parts.find(p => p.type === 'hour').value}:${
        parts.find(p => p.type === 'minute').value}:${
        parts.find(p => p.type === 'second').value}`
    },
    paginationChanged (query) {
      if (query.type === 'page') this.getOrders(query)
      if (query.type === 'size') this.getOrders(query)
      if (query.type === 'sort') this.getOrders(query)
    },
    updateData () {
      const pageSize = this.paginationProps.pageSize || 20
      this.getOrders({ page: 1, pageSize })
    },
    clearSearch () {
      const select = this.$refs.tableSelect
      if (select) {
        select.query = ''
        select.handleQueryChange('')
      }
    },
    searchTables (query) {
      if (!query) {
        this.availableTables = []
        return
      }
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        search: query
      }
      this.loadingTables = true
      // While filters modal is open, use modal selections so table results are constrained immediately.
      const locationIds = this.isFiltersModalOpen ? this.modalSelectedLocationIds : this.selectedLocationIds
      const mapIds = this.isFiltersModalOpen ? this.modalSelectedTableMapIds : this.selectedTableMapIds
      const zoneIds = this.isFiltersModalOpen ? this.modalSelectedZoneIds : this.selectedZoneIds

      if (Array.isArray(locationIds) && locationIds.length) {
        data.location_ids = locationIds
      }
      if (Array.isArray(mapIds) && mapIds.length) {
        data.map_ids = mapIds
      }
      if (Array.isArray(zoneIds) && zoneIds.length) {
        // Some endpoints accept a scalar; keep scalar for 1 item, array for multi.
        data.table_zone_id = zoneIds.length === 1 ? zoneIds[0] : zoneIds
      }

      // Reuse the same table filtering backend as "POS -> Users -> Associate tables"
      this.apiRequest('/table-management', 'GET', data, response => {
        if (response && response.status === true) {
          const arr = Array.isArray(response.data) ? response.data : Object.values(response.data || {})
          this.availableTables = (arr || []).map(t => ({
            ...t,
            label: this.formatTableName(t)
          }))
        } else {
          this.availableTables = []
        }
        this.$nextTick(() => {
          this.loadingTables = false
        })
      })
    },
    formatTableName (t) {
      if (!t) return '-'
      const type = t.type ? String(t.type) : ''
      const number = (t.number !== undefined && t.number !== null && String(t.number).length) ? String(t.number) : ''
      const external = t.external_id ? String(t.external_id) : ''
      const label = [type, number].filter(Boolean).join(' ')
      return label || external || ('ID ' + t.id)
    },
    getPosUsers () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/pos/all/users', 'GET', data, (response) => {
        if (response.status) {
          this.posUsers = response.data.pos_user || []
        }
      })
    },
    getPosDevices () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/pos', 'GET', data, (response) => {
        const map = {}
        if (response.status === true && response.data && response.data.pos) {
          response.data.pos.forEach(p => {
            if (p && p.id != null) {
              map[p.id] = p.name || ('ID ' + p.id)
            }
          })
        }
        this.posNameById = map
      })
    },
    getTableMaps () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      if (Array.isArray(this.selectedLocationIds) && this.selectedLocationIds.length) {
        data.location_ids = this.selectedLocationIds
      }
      this.apiRequest('/pos/all/map', 'GET', data, (response) => {
        if (response && response.status === true) {
          this.tableMaps = response.data.table_maps || []
        } else {
          this.tableMaps = []
        }

        // If location filter changed, some previously selected map ids might no longer exist.
        // Prune stale ids instead of wiping selection immediately.
        const allowed = new Set((this.tableMaps || []).map(m => String(m.id)))
        if (Array.isArray(this.selectedTableMapIds) && this.selectedTableMapIds.length) {
          this.selectedTableMapIds = this.selectedTableMapIds.filter(id => allowed.has(String(id)))
        }
        if (Array.isArray(this.modalSelectedTableMapIds) && this.modalSelectedTableMapIds.length) {
          this.modalSelectedTableMapIds = this.modalSelectedTableMapIds.filter(id => allowed.has(String(id)))
        }
      })
    },
    getZones () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/table-zone', 'GET', data, (response) => {
        if (response && response.status === true && Array.isArray(response.data) && response.data.length) {
          this.zones = response.data.map(z => ({
            id: z.id,
            name: z.name
          }))
        } else {
          this.zones = []
        }
      })
    },
    getOrders (query) {
      if (!this.isPosOrderActionsReport) {
        return
      }
      const pageSize = query && query.pageSize != null ? query.pageSize : (this.paginationProps.pageSize || 20)
      const page = query && query.page != null ? query.page : (this.paginationProps.currentPage || 1)

      if (this.valid_to === '24:00') {
        this.valid_to = '23:59'
      }
      if (this.valid_from === '24:00') {
        this.valid_from = '23:59'
      }

      const fromDate = this.period && this.period[0] ? (this.period[0] + ' ' + (this.valid_from || '00:00')) : null
      const toDate = this.period && this.period[1] ? (this.period[1] + ' ' + (this.valid_to || '23:59')) : null

      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        from_date: fromDate,
        to_date: toDate,
        sort_by: this.sortBy,
        sort_order: this.sortOrder
      }

      if (Array.isArray(this.selectedLocationIds) && this.selectedLocationIds.length) {
        data.location_ids = this.selectedLocationIds
      }
      if (this.selectedPosUser) {
        data.operator = this.selectedPosUser
      }
      if (Array.isArray(this.selectedTableMapIds) && this.selectedTableMapIds.length) {
        data.map_ids = this.selectedTableMapIds
      }
      if (Array.isArray(this.selectedZoneIds) && this.selectedZoneIds.length) {
        // Backend supports filtering by zone; send array when multi-selected.
        data.table_zone_id = this.selectedZoneIds
      }
      if (this.selectedTable && this.selectedTable.id) {
        data.table_id = this.selectedTable.id
      }
      if (this.selectedStatus) {
        data.status = this.selectedStatus
      }
      if (typeof this.selectedActionType === 'number') {
        data.action = this.selectedActionType
      }
      if (this.selectedOrderId != null && String(this.selectedOrderId).trim() !== '') {
        data.id = this.selectedOrderId
      }

      this.loading = true
      this.apiRequest('/reports/v2/order-actions?page=' + page + '&perPage=' + pageSize, 'GET', data, response => {
        if (response.status) {
          const ordersPaginator = response.data && response.data.orders
          const paginatorData = ordersPaginator && Array.isArray(ordersPaginator.data) ? ordersPaginator.data : []

          this.rows = paginatorData

          const pageFromTop = response.pagination && response.pagination.page
          const perPageFromTop = response.pagination && response.pagination.perPage
          const totalFromTop = response.pagination && response.pagination.total

          const pageFromOrders = ordersPaginator && ordersPaginator.current_page
          const perPageFromOrders = ordersPaginator && ordersPaginator.per_page
          const totalFromOrders = ordersPaginator && ordersPaginator.total

          this.total = totalFromTop || totalFromOrders || 0
          this.paginationProps.currentPage = pageFromTop || pageFromOrders || 1
          this.paginationProps.pageSize = perPageFromTop || perPageFromOrders || pageSize
        } else {
          swal(
            '',
            response.errors && response.errors['id'] ? response.errors['id'] : this.$t('validations.wentWrong'),
            'error'
          )
        }
        this.$nextTick(() => {
          this.loading = false
        })
      })
    }
  }
}
</script>

<style>
.centered-cell .cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pos-order-actions-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sales-report .el-table .pos-order-actions-action-btn {
  height: 26px;
  width: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
}

/* Status buttons like OrderView, just smaller */
.sales-report .el-table .pos-order-actions-status-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  font-size: 11px;
  line-height: 18px;
  padding: 0 10px;
  border-radius: 999px !important;
  white-space: nowrap;
  opacity: 1;
  cursor: default;
  pointer-events: none;
  box-shadow: none !important;
  min-width: 0 !important;
  border: 0 !important;
  color: #fff !important;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}

/* Match Orders status colors (local copy; Orders.vue styles are scoped) */
.sales-report .el-table .pos-order-actions-status-btn.NEW { background-color: #0097FC !important; }
.sales-report .el-table .pos-order-actions-status-btn.PROCESSING { background-color: #F3BB45 !important; }
.sales-report .el-table .pos-order-actions-status-btn.PREPARED { background-color: #5e35b1 !important; }
.sales-report .el-table .pos-order-actions-status-btn.DELIVERED { background-color: #7AC29A !important; }
.sales-report .el-table .pos-order-actions-status-btn.CANCELED,
.sales-report .el-table .pos-order-actions-status-btn.NOT_DELIVERED { background-color: #EB5E28 !important; }
.sales-report .el-table .pos-order-actions-status-btn.AWAITING_TRANSPORT { background-color: #ffc107 !important; }
.sales-report .el-table .pos-order-actions-status-btn.PICKED_UP_BY_DELIVERY { background-color: #66615B !important; }
.sales-report .el-table .pos-order-actions-status-btn.TRANSPORT { background-color: #0f566a !important; }
.sales-report .el-table .pos-order-actions-status-btn.ON_HOLD { background-color: #8d5e27 !important; }
.sales-report .el-table .pos-order-actions-status-btn.POSTPONED { background-color: #8d5e27 !important; }

/* Draft orders: match the same purple pattern used for OPEN_DRAFT in Orders.vue */
.sales-report .el-table .pos-order-actions-status-btn.DRAFT,
.sales-report .el-table .pos-order-actions-status-btn.OPEN_DRAFT { background-color: #5e35b1 !important; }

/*
 * POS order-actions filter modal: same DOM as Orders.vue filtersAndSortModal (per-column scroll).
 * Class `sales-report-filters` is on vue-js-modal’s .vm--container.
 */
.vm--container.sales-report-filters .filters-grid.sales-report-filters-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding: 0;
}

.vm--container.sales-report-filters .modal-column-left,
.vm--container.sales-report-filters .modal-column-right {
  padding: 0 10px;
  box-sizing: border-box;
}

.vm--container.sales-report-filters .sales-report-filters-grid .wide-filter {
  grid-column: 1 / 3;
}

.vm--container.sales-report-filters .modal-footer.slim-footer {
  text-align: right;
  margin-top: 8px;
  border-top: 1px solid #eee;
  padding-top: 6px;
  padding-bottom: 0 !important;
  padding-left: 15px !important;
  padding-right: 0 !important;
  margin-bottom: 0 !important;
}

.vm--container.sales-report-filters .slim-footer .el-button {
  font-size: 14px;
}

.vm--container.sales-report-filters .vm--modal {
  border-radius: 6px;
  overflow: hidden !important;
  box-sizing: border-box;
}

.vm--container.scrollable.sales-report-filters .vm--modal {
  margin-bottom: 0 !important;
}

.vm--container.sales-report-filters .vm--modal .card {
  margin-bottom: 0 !important;
  border-radius: 0;
  box-shadow: none;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Reports/Versions/2/PosOrderActionsReport.vue